import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm, User } from 'components/common';
import { FormGroup } from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { CalendarEventExtended, CustomAttributes } from 'shared-types';

interface Props {
  event: CalendarEventExtended;
  setEvent?: React.Dispatch<React.SetStateAction<CalendarEventExtended>>;
}

const CalendarEventParticipantsEditCustomAttributesSelf = ({
  event,
  setEvent
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const { language, messages, user } = useReduxState();

  const handleSubmit = async (values: CustomAttributes) => {
    const isAccessRequest = Boolean(event.accessRequestId);

    const userAttributes = {};

    Object.keys(values).forEach((fieldKey) => {
      if (fieldKey.startsWith('userAttributes-')) {
        const attributeId = fieldKey.split('-')[1];
        userAttributes[attributeId] = values[fieldKey];
      }
    });

    if (isAccessRequest) {
      await api.post(
        `/admin/calendar-events/${event.id}/request-access/${event.accessRequestId}`,
        { userAttributes, userIds: [user.id] }
      );
    } else {
      await api.put(
        `/admin/calendar-events/${event.id}/users-custom-attributes`,
        { userAttributes, userIds: [user.id] }
      );
    }

    const newEvent = {
      ...event,
      reqUserCustomAttributes: { ...userAttributes }
    };
    dispatch.updatePageState({ calendarEvent: newEvent });
    dispatch.addNotice({
      text: messages.userProductCustomAttributeEditSuccess,
      type: 'success'
    });
    if (setEvent) setEvent(newEvent);
  };

  const initialValues = event.userAttributes?.reduce((allFields, field) => {
    allFields[`userAttributes-${field.id}`] =
      event.reqUserCustomAttributes?.[field.id];
    return allFields;
  }, {});

  const available = Date.parse(event.lastJoinDate || event.startDate) > dateNow;

  return event.userAttributes?.length ? (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonDisabled={!available}
      buttonIcon="pen"
      buttonProps={{
        iconStyle: {
          fontSize: '0.9rem',
          marginRight: 3,
          position: 'relative',
          top: -2
        },
        tooltip: available
          ? undefined
          : messages.eventCalendarParticipationDetailsEditInfo
      }}
      buttonSize="small"
      buttonStyle={{
        borderRadius: 30,
        padding: '3px 10px 4px 8px',
        posittion: 'relative',
        top: 1
      }}
      buttonText={messages.commonEdit}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonUpdate}
      modalTitle={messages.userProductCustomAttributeEdit}
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {() => (
        <>
          <User user={user} />

          <div style={{ marginTop: 20 }}>
            {event.userAttributes.map((attr) => {
              const renderTitle =
                attr.titleTranslations?.[language] || attr.title;

              return (
                <FormGroup key={attr.id} label={renderTitle}>
                  <ProductUserCustomAttributeRenderFormField attribute={attr} />
                </FormGroup>
              );
            })}
          </div>
        </>
      )}
    </ModalButtonWithForm>
  ) : null;
};

export default CalendarEventParticipantsEditCustomAttributesSelf;
